import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import BottomNav from '../components/BottomNav';
import TopNav from '../components/TopNav';
import WelcomeBanner from '../components/WelcomeBanner';

const Shop = ({ deviceId, userName }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoTaps, setLogoTaps] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [gameClicks, setGameClicks] = useState(0);
  const [lastGameClickTime, setLastGameClickTime] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // All categories - always visible regardless of products
  const allCategories = ['Hats', 'Hoodies', 'Sweaters', 'Tees', 'Polos', 'Pants', 'Shorts', 'Outerwear', 'Womens', 'Youth'];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const productsData = await api.getProducts();
      
      // Randomize product order on every load
      const shuffled = [...productsData].sort(() => Math.random() - 0.5);
      
      setProducts(shuffled);
    } catch (error) {
      console.error('Error loading shop data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]); // Fixed: Added loadData dependency

  const handleLogoClick = () => {
    const now = Date.now();
    if (now - lastTapTime < 500) {
      const newTaps = logoTaps + 1;
      setLogoTaps(newTaps);
      if (newTaps >= 3) {
        navigate('/admin');
      }
    } else {
      setLogoTaps(1);
    }
    setLastTapTime(now);
  };

  const handleGameTaglineClick = () => {
    const now = Date.now();
    if (now - lastGameClickTime < 2000) {
      const newClicks = gameClicks + 1;
      setGameClicks(newClicks);
      if (newClicks >= 3) {
        navigate('/the-loop-game');
      }
    } else {
      setGameClicks(1);
    }
    setLastGameClickTime(now);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FEFEE3', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
        <TopNav deviceId={deviceId} currentPath={location.pathname} />
        <div style={{ textAlign: 'center' }}>
          <img src="/logo.png" alt="Loading" style={{ width: '100px', height: '100px', imageRendering: 'pixelated', marginBottom: '20px' }} />
          <div style={{ color: '#4C956C', fontSize: '14px', letterSpacing: '0.2em' }}>LOADING SHOP...</div>
        </div>
        <BottomNav currentPath={location.pathname} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FEFEE3', paddingBottom: '100px' }}>
      <TopNav deviceId={deviceId} currentPath={location.pathname} />
      <WelcomeBanner />
      
      {/* Hero Section - Compacted */}
      <div style={{ 
        background: 'linear-gradient(180deg, #2C6E49 0%, #1E4B32 100%)', 
        padding: '30px 20px', 
        textAlign: 'center',
        borderBottom: '4px solid #000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <img 
          src="/logo.png" 
          alt="The Loop Golf" 
          onClick={handleLogoClick}
          style={{ 
            width: '80px', 
            height: '80px', 
            marginBottom: '12px', 
            imageRendering: 'pixelated',
            cursor: 'pointer',
            display: 'block'
          }} 
        />
        <h1 style={{ fontSize: '32px', color: '#FFC857', fontFamily: 'Press Start 2P, monospace', marginBottom: '6px', lineHeight: '1.3' }}>THE LOOP</h1>
        <p style={{ color: '#FEFEE3', fontSize: '11px', letterSpacing: '0.18em', marginBottom: '15px' }}>GOLF CLOTHING CO.</p>
        <div 
          className="retro-card" 
          onClick={handleGameTaglineClick}
          style={{ 
            display: 'inline-block', 
            background: '#2C6E49', 
            padding: '10px 25px', 
            margin: '0',
            cursor: 'pointer',
            transition: 'transform 0.1s',
            border: gameClicks > 0 ? '2px solid #4AE54A' : '2px solid transparent'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <p style={{ color: '#FEFEE3', fontSize: '10px', letterSpacing: '0.12em', margin: 0 }}>PLAY THE GAME. WEAR THE LOOP.</p>
        </div>
      </div>

      {/* Categories */}
      <div style={{ padding: '40px 20px' }}>
        <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '20px', textAlign: 'center' }}>[ SELECT CATEGORY ]</h2>
        <div style={{ display: 'flex', gap: '15px', overflowX: 'auto', paddingBottom: '10px' }} className="hide-scrollbar allow-x-pan">
          {allCategories.map((cat) => (
            <Link 
              key={cat}
              to={`/category/${cat}`}
              className="retro-card"
              style={{ 
                minWidth: '150px',
                padding: '20px',
                textAlign: 'center',
                textDecoration: 'none',
                background: '#FFF'
              }}
            >
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>
                {cat === 'Hats' && '🧢'}
                {cat === 'Hoodies' && '🧥'}
                {cat === 'Sweaters' && '🧶'}
                {cat === 'Tees' && '👔'}
                {cat === 'Polos' && '👕'}
                {cat === 'Pants' && '👖'}
                {cat === 'Shorts' && '🩳'}
                {cat === 'Outerwear' && '🧥'}
                {cat === 'Womens' && '👚'}
                {cat === 'Youth' && '🧒'}
              </div>
              <p style={{ color: '#2C6E49', fontSize: '12px', margin: 0 }}>{cat.toUpperCase()}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
        <h2 style={{ fontSize: '18px', color: '#2C6E49', marginBottom: '30px', textAlign: 'center' }}>[ ALL PRODUCTS ]</h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '20px'
        }}>
          {products.map((product) => {
            // Flip support: find ANY color that has BOTH a FRONT and a BACK tag.
            // Prefer the first variant's color when it qualifies so the shown
            // image stays consistent with the default selected color on the
            // product detail page. Falls back to any other color with a pair.
            const tags = product.image_tags || [];
            const firstColor = product.variants && product.variants[0] ? product.variants[0].color : null;
            const anglesByColor = tags.reduce((acc, t) => {
              if (!t || !t.color) return acc;
              acc[t.color] = acc[t.color] || {};
              if (t.angle === 'front') acc[t.color].front = t.url;
              else if (t.angle === 'back') acc[t.color].back = t.url;
              return acc;
            }, {});
            const flipColor =
              (firstColor && anglesByColor[firstColor] && anglesByColor[firstColor].front && anglesByColor[firstColor].back)
                ? firstColor
                : Object.keys(anglesByColor).find(c => anglesByColor[c].front && anglesByColor[c].back) || null;

            // FRONT image preference: tagged front for flipColor → tagged front for firstColor → variant image → fallback
            const frontForFirst = firstColor && anglesByColor[firstColor] && anglesByColor[firstColor].front;
            const frontForFlip = flipColor ? anglesByColor[flipColor].front : null;
            const displayImage = frontForFlip
              || frontForFirst
              || (product.variants && product.variants[0] && product.variants[0].image_url)
              || (product.images && product.images[0])
              || null;
            const backImage = flipColor ? anglesByColor[flipColor].back : null;
            const hasFlip = !!(displayImage && backImage && flipColor);

            return (
              <Link
                key={product._id}
                to={`/product/${product._id}`}
                className="retro-card"
                data-testid={`product-card-${product._id}`}
                style={{
                  textDecoration: 'none',
                  background: '#FFF',
                  padding: '15px',
                  transition: 'transform 0.2s'
                }}
              >
                <div
                  className={hasFlip ? 'product-card-flip' : ''}
                  style={{
                    background: '#F7F6D8',
                    border: '2px solid #000',
                    aspectRatio: '1',
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {displayImage ? (
                    <>
                      <img
                        src={displayImage}
                        alt={`${product.name}${firstColor ? ` - ${firstColor}` : ''}`}
                        className={hasFlip ? 'product-card-front' : ''}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {hasFlip && (
                        <img
                          src={backImage}
                          alt={`${product.name}${firstColor ? ` - ${firstColor}` : ''} back`}
                          className="product-card-back"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <span style={{ fontSize: '48px' }}>👕</span>
                  )}
                </div>
                <h3 style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '5px', fontWeight: 'bold' }}>{product.name}</h3>
                <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>{product.category}</p>
                <p style={{ fontSize: '14px', color: '#2C2C2C', fontWeight: 'bold' }}>${product.base_price.toFixed(2)}</p>
              </Link>
            );
          })}
        </div>
      </div>

      <BottomNav currentPath={location.pathname} />
    </div>
  );
};

export default Shop;
